import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Filter } from "lucide-react";
import { PRODUCTS } from "@/data/siteData";
import SEO from "@/components/SEO";

const CATEGORIES = ["All", "BOPP Bags", "Polyester Sheets", "Readymade Packaging"];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] } }),
};

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "All";
  const [expanded, setExpanded] = useState(null);
  const products = activeCategory === "All"
    ? PRODUCTS
    : PRODUCTS.filter((p) => p.category === activeCategory);

  const setCategory = (cat) => {
    if (cat === "All") {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
    setExpanded(null);
  };

  return (
    <div data-testid="products-page" className="pt-16">
      <SEO
        title="Products - BOPP Bags, Polyester Sheets & Readymade Packaging"
        description="Explore JSY products including BOPP self-adhesive bags, printed bags, polyester films, and readymade packaging for textile applications."
        image="/images/ld-grade-plastic-packaging.jpeg"
      />
      {/* Hero */}
  <section className="py-12 px-6 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="overline">Our Products</span>
            <h1 className="mt-3 text-5xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
              Packaging & Printing That <span className="text-[#1D4ED8]">Perform</span>
            </h1>
            <p className="mt-4 text-[#71717A] max-w-lg">
              Explore our range of high-quality plastic packaging and printing solutions designed for the textile industry.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter + Grid */}
  <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Category Tabs */}
          <div data-testid="product-filters" className="flex items-center gap-2 mb-10 flex-wrap">
            <Filter size={16} className="text-[#71717A] mr-1" />
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setCategory(cat)}
                className={`px-5 py-2 text-sm font-medium rounded-sm border transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-[#1D4ED8] text-white border-[#1D4ED8]"
                    : "bg-white text-[#71717A] border-[#E4E4E7] hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={`product-item-${i}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="product-card bg-white rounded"
              >
                <div className="h-56 overflow-hidden">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                </div>
                <div className="p-6">
                  <span className="text-xs text-[#1D4ED8] font-medium tracking-wide uppercase">{p.category}</span>
                  <h3 className="mt-2 text-lg tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-[#71717A] leading-relaxed">{p.description}</p>

                  {/* Specifications */}
                  {p.specifications && Object.keys(p.specifications).length > 0 && (
                    <div className="mt-4">
                      <button
                        data-testid={`specs-toggle-${i}`}
                        onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                        className="text-xs text-[#1D4ED8] font-medium tracking-wide uppercase cursor-pointer hover:underline"
                      >
                        {expanded === p.id ? "Hide Specs" : "View Specs"}
                      </button>
                      {expanded === p.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="mt-3 border-t border-[#E4E4E7] pt-3"
                        >
                          <table className="w-full">
                            <tbody>
                              {Object.entries(p.specifications).map(([k, v]) => (
                                <tr key={k} className="border-b border-[#F4F5F7]">
                                  <td className="py-2 text-xs text-[#71717A] font-medium w-1/3">{k}</td>
                                  <td className="py-2 text-xs text-[#09090B]">{v}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </motion.div>
                      )}
                    </div>
                  )}

                  <Link to="/contact" className="mt-4 inline-flex items-center gap-1 text-sm text-[#1D4ED8] font-medium hover:gap-2 transition-all duration-200">
                    Request Quote <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {products.length === 0 && (
            <p className="text-center text-[#71717A] py-12">No products found in this category.</p>
          )}
        </div>
      </section>
    </div>
  );
}
